/**
 * Market date utilities for handling pre-market hours
 *
 * US Stock Market Hours:
 * - Open: 9:30 AM ET (Eastern Time)
 * - Close: 4:00 PM ET
 * - Days: Monday through Friday
 *
 * Before market open, we show the previous trading day's data.
 */

// Market open time in Eastern Time (9:30 AM)
const MARKET_OPEN_HOUR = 9;
const MARKET_OPEN_MINUTE = 30;

/**
 * Get the current time in Eastern Time
 */
function getEasternTime(): Date {
  const now = new Date();
  const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  return new Date(etString);
}

/**
 * Check if it's before market open (9:30 AM ET)
 */
export function isBeforeMarketOpen(): boolean {
  const et = getEasternTime();
  const hours = et.getHours();
  const minutes = et.getMinutes();

  return hours < MARKET_OPEN_HOUR || (hours === MARKET_OPEN_HOUR && minutes < MARKET_OPEN_MINUTE);
}

/**
 * Check if today is a weekend (Saturday = 6, Sunday = 0)
 */
export function isWeekend(): boolean {
  const et = getEasternTime();
  const day = et.getDay();
  return day === 0 || day === 6;
}

/**
 * Get the previous trading day from a given date
 * Skips weekends (Friday -> Thursday, Saturday -> Friday, Sunday -> Friday)
 */
function getPreviousTradingDay(date: Date): Date {
  const prev = new Date(date);
  prev.setDate(prev.getDate() - 1);

  const day = prev.getDay();
  if (day === 0) {
    // Sunday -> go to Friday
    prev.setDate(prev.getDate() - 2);
  } else if (day === 6) {
    // Saturday -> go to Friday
    prev.setDate(prev.getDate() - 1);
  }

  return prev;
}

/**
 * Format a date as YYYY-MM-DD
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the effective market date for data fetching
 *
 * Logic:
 * - If it's a weekend, return the previous Friday
 * - If it's a weekday before 9:30 AM ET, return the previous trading day
 * - Otherwise, return today
 *
 * @returns Date string in YYYY-MM-DD format
 */
export function getEffectiveMarketDate(): string {
  const et = getEasternTime();
  let effectiveDate = new Date(et);

  const day = et.getDay();
  const isWeekendDay = day === 0 || day === 6;
  const beforeOpen = isBeforeMarketOpen();

  if (isWeekendDay) {
    if (day === 0) {
      // Sunday -> Friday (2 days back)
      effectiveDate.setDate(effectiveDate.getDate() - 2);
    } else {
      // Saturday -> Friday (1 day back)
      effectiveDate.setDate(effectiveDate.getDate() - 1);
    }
  } else if (beforeOpen) {
    // Weekday before market open - use previous trading day
    effectiveDate = getPreviousTradingDay(effectiveDate);
  }

  return formatDate(effectiveDate);
}

/**
 * Get today's date in YYYY-MM-DD format (in Eastern Time)
 */
export function getTodayDateET(): string {
  return formatDate(getEasternTime());
}

/**
 * Get milliseconds until market open (9:30 AM ET)
 * Returns 0 if market is already open or it's a weekend
 */
export function getMillisecondsUntilMarketOpen(): number {
  const et = getEasternTime();
  const day = et.getDay();

  // Weekend - return 0
  if (day === 0 || day === 6) return 0;

  // Already past market open
  if (!isBeforeMarketOpen()) return 0;

  // Calculate ms until 9:30 AM ET
  const marketOpenToday = new Date(et);
  marketOpenToday.setHours(MARKET_OPEN_HOUR, MARKET_OPEN_MINUTE, 0, 0);

  return marketOpenToday.getTime() - et.getTime();
}

/**
 * Check if the market just opened and the data pipeline is still warming up.
 * Returns true during the first few minutes after market open (9:30 - 9:33 AM ET).
 * Widgets use this to show a "warming up" message instead of an error.
 */
export function isMarketWarmup(): boolean {
  if (isWeekend()) return false;
  const et = getEasternTime();
  const hours = et.getHours();
  const minutes = et.getMinutes();

  if (hours === MARKET_OPEN_HOUR && minutes >= MARKET_OPEN_MINUTE && minutes < MARKET_OPEN_MINUTE + 3) {
    return true;
  }
  return false;
}

/**
 * Check if we're showing previous day's data
 */
export function isShowingPreviousDay(): boolean {
  const et = getEasternTime();
  const day = et.getDay();

  // Weekend always shows previous
  if (day === 0 || day === 6) return true;

  // Weekday before market open
  return isBeforeMarketOpen();
}

/**
 * Format a date for display (e.g., "Dec 13")
 */
export function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
